import { useConsentManager } from "@c15t/react";
import { ReactNode, useEffect, useRef } from "react";

import { useConsentBannerMutation, useConsentPreferencesQuery } from "../hooks";

interface ConsentSyncProviderProps {
  children: ReactNode;
}

/**
 * Provider that syncs server-side consent to c15t on initial load.
 * Enables consent persistence for both authenticated and anonymous users.
 * Also sets default consent values when no stored consent exists.
 * Must be rendered inside ConsentManagerProvider.
 */
export const ConsentSyncProvider = ({ children }: ConsentSyncProviderProps) => {
  const { consents, setSelectedConsent, hasConsented, isLoadingConsentInfo, saveConsents } =
    useConsentManager();

  const { data: serverConsent, isLoading: isLoadingServerConsent } = useConsentPreferencesQuery();
  const { mutate: syncConsentToBackend } = useConsentBannerMutation();
  const hasSyncedToServer = useRef(false);
  const hasPatchedMeasurement = useRef(false);
  const hadConsentOnMount = useRef(hasConsented());

  const hasServerConsent = !!serverConsent && Object.keys(serverConsent).length > 0;

  // Re-sync local consent to backend when server-side session expires (14 days)
  // but c15t local storage still has the user's choices.
  useEffect(() => {
    if (hasSyncedToServer.current || isLoadingConsentInfo || isLoadingServerConsent) {
      return;
    }
    // Uses hadConsentOnMount to avoid firing when consent was just written this
    // session (e.g. banner "Accept All" or GPC sync), which would cause a
    // duplicate POST or overwrite the correct consent source attribution.
    if (hadConsentOnMount.current && !hasServerConsent) {
      syncConsentToBackend({
        experience: consents.experience,
        functionality: consents.functionality,
        marketing: consents.marketing,
      });
      hasSyncedToServer.current = true;
    }
  }, [
    consents,
    hasServerConsent,
    isLoadingServerConsent,
    isLoadingConsentInfo,
    syncConsentToBackend,
  ]);

  // One-time migration: backfill `measurement` for visitors who consented before
  // it was added as a category, so analytics_storage is no longer denied in GTM.
  useEffect(() => {
    if (hasPatchedMeasurement.current || isLoadingConsentInfo) {
      return;
    }

    if (hasConsented() && consents.marketing && !consents.measurement) {
      setSelectedConsent("measurement", true);
      saveConsents("custom");
      hasPatchedMeasurement.current = true;
    }
  }, [hasConsented, consents, setSelectedConsent, saveConsents, isLoadingConsentInfo]);

  // Hydrate c15t from server consent on initial load, or apply defaults when
  // no consent record exists anywhere (banner will still appear).
  useEffect(() => {
    if (isLoadingConsentInfo || isLoadingServerConsent || hasConsented()) {
      return;
    }

    if (hasServerConsent) {
      setSelectedConsent("necessary", true);
      setSelectedConsent("marketing", serverConsent.accept_all_cookies);
      setSelectedConsent("measurement", serverConsent.accept_all_cookies);
      setSelectedConsent("experience", !serverConsent.sell_share_opt_out);
      setSelectedConsent("functionality", !serverConsent.limit_sensitive_data);
      saveConsents("custom");
    } else {
      setSelectedConsent("necessary", true);
      setSelectedConsent("marketing", true);
      setSelectedConsent("measurement", true);
      setSelectedConsent("experience", true);
      setSelectedConsent("functionality", true);
    }
  }, [
    isLoadingServerConsent,
    hasServerConsent,
    serverConsent,
    setSelectedConsent,
    hasConsented,
    isLoadingConsentInfo,
    saveConsents,
  ]);

  return <>{children}</>;
};
